import clsx from 'clsx';

import { BILLING_REFUND_POLICY_HREF } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSLink } from '@repo/sds/SDSLink';
import { SDSText } from '@repo/sds/SDSText';

/**
 * Where the money-back terms are written down — Section 5 of the Terms of Use.
 * The refund-details frame draws it as a bare, underlined line of small text.
 */
export function RefundPolicyLink() {
  return (
    <SDSFlexbox className="RefundPolicyLink" direction="row">
      <SDSLink
        className="RefundPolicyNote__link"
        href={BILLING_REFUND_POLICY_HREF}
        target="_blank"
        dataTestId={DataTestIds.BILLING.REFUND.POLICY_LINK}
      >
        <SDSText type="button-medium" text="View refund policy" color="text-secondary" isInline />
      </SDSLink>
    </SDSFlexbox>
  );
}

interface RefundPolicyNoteProps {
  /** Two centred lines beside the package card's Stripe line, or one in a dialog. */
  layout: 'stacked' | 'inline';
  className?: string;
  dataTestId: string;
  linkDataTestId: string;
}

/**
 * "Subject to StartHub’s Refund Policy which can be found in Terms of Use".
 *
 * Only "Terms of Use" is a link, as in every frame: the policy is a section of
 * that document rather than a page of its own, and the headings there carry no
 * anchors to deep-link to — so the document is the honest destination.
 */
export function RefundPolicyNote({ layout, className, dataTestId, linkDataTestId }: RefundPolicyNoteProps) {
  const link = (
    <SDSLink
      className="RefundPolicyNote__link"
      href={BILLING_REFUND_POLICY_HREF}
      target="_blank"
      dataTestId={linkDataTestId}
    >
      Terms of Use
    </SDSLink>
  );

  if (layout === 'inline') {
    return (
      <SDSFlexbox
        className={clsx('RefundPolicyNote--inline', className)}
        direction="row"
        justifyContent="center"
        fullWidth
        dataTestId={dataTestId}
      >
        <SDSText
          type="button-medium"
          align="center"
          color="text-secondary"
          text={<>Subject to StartHub’s Refund Policy which can be found in {link}</>}
        />
      </SDSFlexbox>
    );
  }

  return (
    <SDSFlexbox className={className} direction="column" alignItems="center" dataTestId={dataTestId}>
      <SDSText type="button-medium" text="Subject to StartHub’s Refund Policy" color="text-secondary" />
      <SDSText type="button-medium" color="text-secondary" text={<>which can be found in {link}</>} />
    </SDSFlexbox>
  );
}
